/**
 * Dashboard Share API Client (Dashboard Portal feature)
 *
 * CLONED from two source files and adapted for the dashboard model:
 *   - `./portalPublicApi.ts`  → the public, unauthenticated viewer fetchers
 *     (fetchDashboardShareMeta / View / verifyPassword) hitting
 *     `/api/dashboard-share-view/:token/*`. Same raw-fetch / no-Authorization /
 *     `credentials: "include"` / 10s-timeout contract as the portal viewer.
 *   - `./portalLinksApi.ts`   → the agency-side, authenticated CRUD client
 *     (create / list / revoke / regenerate) hitting `/api/dashboard-share-links`.
 *
 * v1 SCOPE: dashboards have a SINGLE share scope "dashboard" (no report-style
 * "full"/"report" variant). The create input carries `dashboardId` directly —
 * there is NO `scope`/`reportId` discriminator (mirrors the server's
 * createDashboardShareLinkSchema).
 *
 * The public viewer fetchers reuse the SAME response types as the portal viewer
 * (`PortalMeta` / `PortalViewResponse` / `PortalVerifyPasswordResponse`) because
 * the dashboard-share-view server routes return byte-identical shapes (the server
 * reports scope "full" so the existing PortalShell renders it unchanged).
 */

import type {
  PortalMeta,
  PortalViewResponse,
  PortalMetaInactive,
  PortalVerifyPasswordResponse,
  PortalDateRangePreset,
  PortalComparisonPreset,
} from "@provenleap/types";
import { apiGet, apiPost, apiDelete } from "./apiClient";

// ============================================================================
// Public viewer — types (cloned from portalPublicApi.ts)
// ============================================================================

export interface FetchDashboardShareViewOptions {
  dateRange?: PortalDateRangePreset;
  comparison?: PortalComparisonPreset;
  /** When true, appends `?refresh=1` to bypass the server-side 60s KV cache. */
  refresh?: boolean;
}

/** Result of a verify-password call — includes HTTP status for 429 handling. */
export interface VerifyDashboardSharePasswordResult {
  status: number;
  body: PortalVerifyPasswordResponse | { error: string };
  /** When status === 429, parsed from the `Retry-After` header (in seconds). */
  retryAfter?: number;
}

/** Error shape surfaced to callers when fetch itself fails (network, timeout). */
export class DashboardShareApiError extends Error {
  constructor(
    message: string,
    public readonly kind: "network" | "timeout" | "bad_response",
  ) {
    super(message);
    this.name = "DashboardShareApiError";
  }
}

// ============================================================================
// Public viewer — constants
// ============================================================================

/** Fetch timeout (matches portalPublicApi). 10s = the server per-platform budget. */
const DASHBOARD_SHARE_FETCH_TIMEOUT_MS = 10_000;

// ============================================================================
// Public viewer — fetchers
// ============================================================================

/**
 * Fetch the meta bootstrap response for a dashboard share token.
 *
 * Returns the full `PortalMeta` union — callers switch on `status`:
 *   - `"inactive"` → render `<PortalInactivePage />`
 *   - `"active"` with `passwordProtected === true` → render password gate
 *   - `"active"` with `passwordProtected === false` → proceed to `/view`
 */
export async function fetchDashboardShareMeta(token: string): Promise<PortalMeta> {
  const url = `/api/dashboard-share-view/${encodeURIComponent(token)}/meta`;
  const res = await safeFetch(url, { method: "GET", credentials: "include" });
  return parseJsonOrThrow<PortalMeta>(res);
}

/**
 * Submit a password for a dashboard share link.
 *
 * Returns `{ status, body, retryAfter? }` so callers can branch exactly like the
 * portal password gate (200 verified / 200 not-verified / 429 rate-limited).
 */
export async function verifyDashboardSharePassword(
  token: string,
  password: string,
): Promise<VerifyDashboardSharePasswordResult> {
  const url = `/api/dashboard-share-view/${encodeURIComponent(token)}/verify-password`;
  const res = await safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });

  let body: PortalVerifyPasswordResponse | { error: string };
  try {
    body = (await res.json()) as PortalVerifyPasswordResponse | { error: string };
  } catch {
    throw new DashboardShareApiError(
      "Invalid JSON response from verify-password",
      "bad_response",
    );
  }

  const retryAfterHeader = res.headers.get("Retry-After");
  const retryAfter = retryAfterHeader
    ? Number.parseInt(retryAfterHeader, 10)
    : undefined;

  return {
    status: res.status,
    body,
    ...(Number.isFinite(retryAfter) ? { retryAfter } : {}),
  };
}

/**
 * Fetch dashboard share view data.
 *
 * Returns a discriminated union mirroring the portal viewer:
 *   - `PortalViewResponse` (status === "active") → render the shell
 *   - `PortalMetaInactive` (status === "inactive") → flip to inactive page
 *
 * A 401 `{ error: "password_required" }` is thrown as a
 * `DashboardShareApiError("password_required", "bad_response")` so callers can
 * re-open the password gate.
 */
export async function fetchDashboardShareView(
  token: string,
  options: FetchDashboardShareViewOptions = {},
): Promise<PortalViewResponse | PortalMetaInactive> {
  const params = new URLSearchParams();
  if (options.dateRange) params.set("dateRange", options.dateRange);
  if (options.comparison) params.set("comparison", options.comparison);
  if (options.refresh) params.set("refresh", "1");

  const qs = params.toString();
  const url = `/api/dashboard-share-view/${encodeURIComponent(token)}/view${
    qs ? "?" + qs : ""
  }`;
  const res = await safeFetch(url, { method: "GET", credentials: "include" });

  if (res.status === 401) {
    throw new DashboardShareApiError("password_required", "bad_response");
  }

  return parseJsonOrThrow<PortalViewResponse | PortalMetaInactive>(res);
}

// ============================================================================
// Public viewer — PortalShell / PortalPasswordGate backend seam
// ============================================================================

/**
 * Backend objects wiring the REUSED PortalShell / PortalPasswordGate components
 * to the dashboard-share-view endpoints instead of the report portal-view ones.
 *
 * Both components default to the report portal's `portalPublicApi`; the dashboard
 * public route (`dashboard-view.$token.tsx`) passes these via the `api` prop so
 * widgets load + passwords verify against `/api/dashboard-share-view/*`.
 *
 * The `isPasswordRequiredError` / `isTimeoutError` predicates match on
 * `DashboardShareApiError` (this module's error class) so the shared components
 * never depend on `portalPublicApi`'s `PortalApiError` for the dashboard surface.
 */
export const dashboardShareShellBackend = {
  fetchView: (token: string, options: FetchDashboardShareViewOptions) =>
    fetchDashboardShareView(token, options),
  /**
   * Dashboards have no per-dashboard PDF — meta always reports
   * `scope: "full"` + `downloadable: false`, so PortalShell never renders the
   * download button and this is never invoked. Returning the (non-existent)
   * download path keeps the seam total without a special-case in the component.
   */
  downloadUrl: (token: string) =>
    `/api/dashboard-share-view/${encodeURIComponent(token)}/download`,
  isPasswordRequiredError: (error: unknown) =>
    error instanceof DashboardShareApiError &&
    error.message === "password_required",
};

export const dashboardSharePasswordGateBackend = {
  verifyPassword: (token: string, password: string) =>
    verifyDashboardSharePassword(token, password),
  isTimeoutError: (error: unknown) =>
    error instanceof DashboardShareApiError && error.kind === "timeout",
};

// ============================================================================
// Public viewer — internal helpers
// ============================================================================

async function safeFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, {
      ...init,
      signal: AbortSignal.timeout(DASHBOARD_SHARE_FETCH_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new DashboardShareApiError("Request timed out", "timeout");
    }
    throw new DashboardShareApiError(
      err instanceof Error ? err.message : "Network error",
      "network",
    );
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    throw new DashboardShareApiError("Invalid JSON response", "bad_response");
  }
}

// ============================================================================
// Agency-side CRUD — types (cloned from portalLinksApi.ts)
// ============================================================================

export type DashboardShareLinkStatus = "active" | "expired" | "revoked";
export type DashboardShareExpirationPreset = "7d" | "30d" | "90d" | "never";

/** Mirror of the server `DashboardShareLinkProjection` shape (no scope discriminator). */
export interface DashboardShareLinkProjection {
  id: string;
  clientId: string;
  dashboardId: string;
  label: string;
  scope: string;
  tokenPrefix: string;
  hiddenMetricsJson: string | null;
  hiddenIntegrationsJson: string | null;
  expiresAt: number | null;
  passwordProtected: boolean;
  createdBy: string | null;
  revokedAt: number | null;
  lastAccessedAt: number | null;
  accessCount: number;
  createdAt: number;
  updatedAt: number;
  status: DashboardShareLinkStatus;
}

export interface DashboardShareLinkWithToken extends DashboardShareLinkProjection {
  /** Raw token — surfaced exactly once on create/regenerate. Treat as a secret. */
  token: string;
  /** Full constructed share URL ready to copy and share. */
  url: string;
}

/** Create input — v1 single scope, carries `dashboardId` directly (no scope field). */
export interface CreateDashboardShareLinkInput {
  clientId: string;
  dashboardId: string;
  label: string;
  expiresAt: DashboardShareExpirationPreset;
  password?: string;
  hiddenMetrics?: string[];
  hiddenIntegrations?: string[];
}

interface ApiError {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  errors?: Array<{ field: string; message: string }>;
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    const message = err.detail ?? err.title ?? `Request failed (${res.status})`;
    const error = new Error(message) as Error & {
      status?: number;
      apiError?: ApiError;
    };
    error.status = res.status;
    error.apiError = err;
    throw error;
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

// ============================================================================
// Agency-side CRUD — fetchers
// ============================================================================

export async function listDashboardShareLinks(
  clientId: string,
): Promise<DashboardShareLinkProjection[]> {
  const res = await apiGet(
    `/api/dashboard-share-links?clientId=${encodeURIComponent(clientId)}`,
  );
  return unwrap<DashboardShareLinkProjection[]>(res);
}

export async function createDashboardShareLink(
  input: CreateDashboardShareLinkInput,
): Promise<DashboardShareLinkWithToken> {
  const res = await apiPost(`/api/dashboard-share-links`, input);
  return unwrap<DashboardShareLinkWithToken>(res);
}

export async function revokeDashboardShareLink(id: string): Promise<void> {
  const res = await apiDelete(`/api/dashboard-share-links/${id}`);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    throw new Error(err.detail ?? err.title ?? "Failed to revoke link");
  }
}

export async function regenerateDashboardShareLinkToken(
  id: string,
): Promise<DashboardShareLinkWithToken> {
  const res = await apiPost(`/api/dashboard-share-links/${id}/regenerate-token`, {});
  return unwrap<DashboardShareLinkWithToken>(res);
}

// ============================================================================
// Agency-side CRUD — formatting helpers (cloned from portalLinksApi.ts)
// ============================================================================

/**
 * Format a relative date string for "expires in X days" UX. Falls back to an
 * absolute date when the diff is more than 90 days for clarity.
 */
export function formatDashboardShareExpiration(
  expiresAt: number | null,
  now: number = Date.now(),
): string {
  if (expiresAt === null) return "Never expires";
  const diff = expiresAt - now;
  if (diff <= 0) return "Expired";
  const days = Math.round(diff / 86_400_000);
  if (days <= 1) return "Expires today";
  if (days <= 30) return `Expires in ${days} days`;
  if (days <= 90) return `Expires in ${Math.round(days / 7)} weeks`;
  return `Expires ${new Date(expiresAt).toLocaleDateString()}`;
}
